import { useEffect, useRef, useState } from "react";
import { Play, Pause, Radio, Volume2 } from "lucide-react";
import { STATIONS, useStationPlayer } from "./phoneMusic";

/* Boombox — the city radio. Four stations, all synthesized live by the shared
   engine (phoneMusic.js) — real audio, zero samples, royalty-free by design.
   The same feed plays through the in-car radio HUD. */

export default function RadioApp({ accent, sfx }) {
  const [station, setStation] = useState(STATIONS[0]);
  const [trackIdx, setTrackIdx] = useState(0);
  const [vol, setVol] = useState(0.7);
  const { playingId, play, stop, setVolume } = useStationPlayer();
  const playing = playingId === station.id;
  const timer = useRef(null);

  // auto-tune the moment the app opens; engine stops when the app closes
  useEffect(() => { play(STATIONS[0].id, 0.7); return () => stop(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (playing) timer.current = setInterval(() => setTrackIdx((i) => i + 1), 9000);
    return () => clearInterval(timer.current);
  }, [playing, station]);

  const track = station.tracks[trackIdx % station.tracks.length];

  const tune = (s) => {
    sfx?.keyTick();
    setStation(s); setTrackIdx(0);
    play(s.id, vol);
  };
  const toggle = () => {
    sfx?.keyTick();
    if (playing) stop(); else play(station.id, vol);
  };
  const onVol = (v) => { setVol(v); setVolume(v); };

  return (
    <div data-testid="radio-app" className="p-3 flex flex-col gap-3 min-h-full">
      <style>{`@keyframes phEq{0%,100%{transform:scaleY(0.25)}50%{transform:scaleY(1)}}`}</style>
      <div className="flex items-center gap-2 px-1 pb-1">
        <Radio className="w-4 h-4" style={{ color: station.color }} />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">Boombox</p>
        <p className="ml-auto font-mono text-[7px] text-emerald-300/80 uppercase tracking-[0.1em]">live audio</p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-end justify-between">
          <p className="font-sans font-bold text-3xl text-white">{station.freq}<span className="text-sm text-white/40 ml-1">FM</span></p>
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] px-2 py-1 rounded-full border" style={{ color: station.color, borderColor: `${station.color}55` }}>{station.genre}</span>
        </div>
        <p className="font-sans font-semibold text-sm text-white mt-1">{station.name}</p>
        <p data-testid="radio-track" className="font-mono text-[9px] text-white/50 mt-0.5 truncate">{playing ? `♪ ${track}` : "— paused —"}</p>
        {/* equaliser */}
        <div className="flex items-end gap-1 h-10 mt-3">
          {Array.from({ length: 14 }, (_, i) => (
            <span key={i} className="flex-1 rounded-sm origin-bottom"
              style={{ backgroundColor: station.color, opacity: playing ? 0.85 : 0.15,
                       animation: playing ? `phEq ${0.5 + (i % 5) * 0.13}s ease-in-out ${i * 0.07}s infinite` : "none",
                       height: "100%", transform: playing ? undefined : "scaleY(0.15)" }} />
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button data-testid="radio-play" onClick={toggle}
            className="w-12 h-12 rounded-full flex items-center justify-center text-black transition-transform active:scale-90 shrink-0"
            style={{ backgroundColor: station.color }}>
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <div className="flex-1 flex items-center gap-2">
            <Volume2 className="w-3.5 h-3.5 text-white/40 shrink-0" />
            <input data-testid="radio-volume" type="range" min="0" max="1" step="0.05" value={vol}
              onChange={(e) => onVol(parseFloat(e.target.value))}
              className="flex-1 h-1 accent-pink-400 cursor-pointer" />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {STATIONS.map((s) => (
          <button key={s.id} data-testid={`radio-station-${s.id}`} onClick={() => tune(s)}
            className={`rounded-xl border px-3.5 py-2.5 flex items-center gap-3 text-left transition-colors ${station.id === s.id ? "bg-white/10 border-white/25" : "bg-white/[0.03] border-white/10 hover:bg-white/5"}`}>
            <span className="font-mono text-[10px] font-bold w-10" style={{ color: s.color }}>{s.freq}</span>
            <span className="flex-1 font-sans text-xs text-white">{s.name}</span>
            {station.id === s.id && playing
              ? <span className="font-mono text-[7px] uppercase tracking-[0.1em] text-emerald-300 animate-pulse">on air</span>
              : <span className="font-mono text-[7px] uppercase tracking-[0.1em] text-white/40">{s.genre}</span>}
          </button>
        ))}
      </div>
      <p className="mt-auto font-mono text-[7px] text-white/30 text-center uppercase tracking-[0.2em]">generated live on your handset · royalty-free by design · same feed on the car radio</p>
    </div>
  );
}
